import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAllMitras, getSalesByStatus } from '../../supabase/database';
import { FaBell, FaUsers, FaChartLine, FaCheckCircle, FaUserShield } from 'react-icons/fa';

// Memoized StatCard untuk mencegah re-render yang tidak perlu
const StatCard = memo(({ icon, title, value, color, loading }) => {
  const colorClass = useMemo(() => {
    const colors = {
      blue: 'from-blue-500 to-blue-400',
      green: 'from-green-500 to-green-400',
      yellow: 'from-yellow-500 to-yellow-400',
      red: 'from-red-500 to-red-400',
    };
    return colors[color] || colors.blue;
  }, [color]);

  return (
    <div className={`bg-gradient-to-br ${colorClass} text-white p-6 rounded-xl shadow-md flex items-center justify-between`}>
      <div>
        <p className="text-lg font-semibold">{title}</p>
        <p className="text-4xl font-bold">
          {loading ? '...' : value}
        </p>
      </div>
      <div className="text-5xl opacity-50">{icon}</div>
    </div>
  );
});

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    pendingOrders: 0,
    totalMitra: 0,
    monthlyOmset: 0,
    completedOrders: 0,
  });
  const [loading, setLoading] = useState(true);

  // Memoized fetchAdminStats untuk mencegah re-creation
  const fetchAdminStats = useCallback(async () => {
    try {
      setLoading(true);
      const [pendingData, mitraData, completedData] = await Promise.all([
        getSalesByStatus('menunggu validasi'),
        getAllMitras(),
        getSalesByStatus('divalidasi')
      ]);

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      // Optimized monthly calculation with memoization
      const monthlyOmset = (completedData || [])
        .filter(sale => {
          if (!sale.createdAt) return false;
          const saleDate = sale.createdAt instanceof Date ? sale.createdAt : new Date(sale.createdAt);
          return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
        })
        .reduce((total, sale) => total + (Number(sale.subtotal) || 0), 0);

      setStats({
        pendingOrders: (pendingData || []).length,
        totalMitra: (mitraData || []).length,
        monthlyOmset,
        completedOrders: (completedData || []).length,
      });
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      setStats({
        pendingOrders: 0,
        totalMitra: 0,
        monthlyOmset: 0,
        completedOrders: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminStats();
  }, [fetchAdminStats]);

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      <div className="bg-yellow-500 text-white py-2 px-4 mb-6 rounded-md overflow-hidden">
        <marquee behavior="scroll" direction="left" scrollamount="3">
          🎉 Selamat datang di Dashboard Admin NH Press! Kelola mitra dan pesanan dengan mudah. 📊
        </marquee>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Selamat datang, {currentUser?.nama || currentUser?.nama_mitra || currentUser?.email || 'Admin'}!
        </h1>
        <p className="text-gray-600">Berikut adalah ringkasan aktivitas terbaru di platform NH Press.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<FaBell />}
          title="Pesanan Pending"
          value={stats.pendingOrders}
          color="yellow"
          loading={loading}
        />
        <StatCard
          icon={<FaUsers />}
          title="Total Mitra"
          value={stats.totalMitra}
          color="blue"
          loading={loading}
        />
        <StatCard
          icon={<FaChartLine />}
          title="Omset Bulan Ini"
          value={`Rp ${stats.monthlyOmset.toLocaleString('id-ID')}`}
          color="green"
          loading={loading}
        />
        <StatCard
          icon={<FaCheckCircle />}
          title="Pesanan Selesai"
          value={stats.completedOrders}
          color="red"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-6 rounded-xl">
          <h2 className="text-xl font-bold mb-4 text-gray-700">Ringkasan Sistem</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <FaUserShield className="text-blue-500" />
              <span className="text-gray-700">Status: Admin Dashboard Aktif</span>
            </div>
            <div className="flex items-center gap-3">
              <FaChartLine className="text-green-500" />
              <span className="text-gray-700">Sistem: Berjalan Normal</span>
            </div>
            <div className="flex items-center gap-3">
              <FaBell className="text-yellow-500" />
              <span className="text-gray-700">Notifikasi: {stats.pendingOrders} pesanan menunggu validasi</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl">
          <h2 className="text-xl font-bold mb-4 text-gray-700">Aktivitas Terbaru</h2>
          {loading ? <p>Memuat aktivitas...</p> : <p className="text-gray-500">Komponen untuk daftar aktivitas akan ditampilkan di sini...</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
